/**
 * 메뉴와 경로를 버튼 키로 가른다(명세 0-5).
 *
 * ⚠ **이것은 편의이지 보안이 아니다**(레거시 원칙 그대로). 여기서 숨긴 화면도
 * 주소창으로 열 수 있고, 열어도 백엔드가 403 을 돌려준다. **최종 판단은 서버**다.
 *
 * ⚠ **역할 이름·권한 이름으로 유추하지 않는다.** 「리스크 관리자니까 리스크 메뉴」가
 * 아니라 「`risk.view` 를 가졌으니 리스크 메뉴」다. 권한 이름의 키는 최고관리자가
 * 언제든 고치고, 고치는 순간 이름으로 짠 화면은 거짓말을 시작한다.
 *
 * 원천은 XM-06 의 `superAdmin`·`keys` 다. 최고관리자는 모든 키를 가진다. 직원 관리·
 * 권한 이름은 키가 아니라 `superAdmin` 으로 가린다(계약 7절 머리말).
 */
import type { ButtonKey } from '@/api/types'

/** XM-06 에서 온 것 가운데 메뉴·버튼을 가르는 둘. */
export interface Access {
  superAdmin: boolean
  keys: readonly string[]
}

export interface NavItem {
  path: string
  label: string
  /** 키 가운데 **하나라도** 있으면 보인다. `'superAdmin'` 이면 최고관리자만. */
  needs: readonly ButtonKey[] | 'superAdmin'
  /** 화면을 아직 이 저장소에 만들지 않은 것. 메뉴에 안 나온다. */
  pending?: boolean
}

/** 명세 0-5 표 — 순서도 그대로다. */
export const NAV: readonly NavItem[] = [
  { path: '/users', label: 'Members', needs: ['members.view'] },
  // 2026-10-12 (물음 16 ⑤ — 사장님 「… 거래소 관리자에 KYC인증 메뉴에서 관리자가 직접 보고 가입 승인을 할 수 있도록」): 옛
  // 「Sign-up approvals」 자리. 가입 승인 키 둘(`signups.view`·`signups.review`)이 이 메뉴의 키다.
  { path: '/kyc-verification', label: 'KYC verification', needs: ['signups.view'] },
  { path: '/partner-applications', label: 'Partner applications', needs: ['partner_applications.view'] },
  // ⚠ **needs 는 「하나라도」다.** 보증금 한쪽 탭 키만 가진 사람에게도 메뉴가 보이고,
  // 안에서는 그 키의 탭만 그려진다.
  { path: '/collateral', label: 'Collateral', needs: ['collateral.view', 'withdrawals.view'] },
  { path: '/master-revocations', label: 'Master revocations', needs: ['revocations.view'] },
  { path: '/audit-logs', label: 'Audit logs', needs: ['audit.view'] },
  // 리스크(EA-41) — 레거시 pending 자리. 계약 XM-62~XM-68 이 있어 만들었다.
  { path: '/risk', label: 'Risk', needs: ['risk_queue.view', 'risk.view'] },
  { path: '/incidents', label: 'Incidents', needs: ['incidents.view'] },
  { path: '/trades', label: 'Trade monitor', needs: ['trades.view'] },
  { path: '/staff', label: 'Staff', needs: 'superAdmin' },
  // 시스템 설정(EA-42) — 레거시 pending 자리. 계약 XM-83~XM-89 가 있어 만들었다.
  { path: '/settings', label: 'System settings', needs: ['settings.view', 'terms.view'] },
  // 보관·파기(EA-54) — R-XAD-06(원문 14, 확인 40·42). 계약 XM-76~XM-82.
  { path: '/records', label: 'Retention & archives', needs: ['retention.view', 'archives.view'] },
  // 권한 이름(EA-35) — 레거시 pending 「관리자 관리」(/admins) 자리.
  { path: '/permission-names', label: 'Permission names', needs: 'superAdmin' },
]

/** 이 키를 가졌는가. 최고관리자는 모든 키를 가진다(계약 7절). */
export function holds(access: Access, key: ButtonKey): boolean {
  return access.superAdmin || access.keys.includes(key)
}

function allowed(item: NavItem, access: Access): boolean {
  if (item.needs === 'superAdmin') return access.superAdmin
  return item.needs.some((k) => holds(access, k))
}

/** 이 사람이 볼 수 있는 메뉴. */
export function visibleNav(access: Access): NavItem[] {
  return NAV.filter((n) => !n.pending && allowed(n, access))
}

/** 이 경로에 들어갈 수 있는가. NAV 에 없는 경로는 막지 않는다(레거시 그대로 — 없는 화면이 답한다). */
export function mayEnter(path: string, access: Access): boolean {
  const item = NAV.find((n) => path === n.path || path.startsWith(n.path + '/'))
  if (!item) return true
  return allowed(item, access)
}
